import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader, Database, GitBranch, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import RecordDetailModal from '../components/RecordDetailModal';
import KnowledgeLibrary from '../components/KnowledgeLibrary';
import Tabs from '../components/Tabs';
import { InlineError } from '../components/InlineError';
import { renderValue } from '../lib/knowledge';
import SourceLink from '../components/SourceLink';
import type {
    KnowledgeLabelSummary, KnowledgeLabel, KnowledgeRelationshipType,
    KnowledgeRecord, KnowledgeSuggestion,
} from '../lib/types';
import OrganizationRole from '../components/OrganizationRole';

interface LabelDetail {
    label: KnowledgeLabel;
    records: KnowledgeRecord[];
}

function RecordsTable({ detail, onOpen, picked, onPick }: {
    detail: LabelDetail;
    onOpen: (key: string) => void;
    picked: string[];
    onPick: (key: string) => void;
}) {
    const columns = Object.keys(detail.label.shape.properties ?? {});

    if (detail.records.length === 0) {
        return (
            <p className="navaris-empty">
                No {detail.label.name.toLowerCase()} records yet — they appear once a connected
                source is synced.
            </p>
        );
    }

    return (
        <div className="overflow-auto max-h-[65vh] border border-softgrey rounded-xl">
            <table className="w-full text-[13px]">
                <thead className="sticky top-0 z-10">
                    <tr className="bg-offwhite text-left text-coolslate">
                        <th className="px-3 py-2 font-medium w-8">
                            <span className="sr-only">Select</span>
                        </th>
                        {columns.map(c => (
                            <th key={c} className="px-3 py-2 font-medium whitespace-nowrap">{c}</th>
                        ))}
                        <th className="px-3 py-2 font-medium whitespace-nowrap">sources</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-softgrey">
                    {detail.records.map(record => (
                        <tr
                            key={record.key}
                            onClick={() => onOpen(record.key)}
                            className="text-graphite cursor-pointer hover:bg-offwhite/60 transition-colors"
                        >
                            <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    aria-label={`Select ${record.name}`}
                                    checked={picked.includes(record.key)}
                                    onChange={() => onPick(record.key)}
                                    className="cursor-pointer"
                                />
                            </td>
                            {columns.map((c, i) => (
                                <td key={c} className="px-3 py-2 whitespace-nowrap">
                                    {renderValue(record.properties[c])}
                                    {i === 0 && <OrganizationRole role={record.ours} />}
                                    {i === 0 && record.pending && <span className="ml-2"><PendingBadge record={record} /></span>}
                                </td>
                            ))}
                            <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {record.sources.map((source, i) => (
                                        <SourceLink key={i} source={source} onClick={e => e.stopPropagation()} />
                                    ))
                                        .flatMap((el, i) => i === 0 ? [el] : [<span key={`s${i}`} className="text-coolslate/50">·</span>, el])}
                                    {record.is_consolidated && (
                                        <span className="px-1.5 py-0.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium">
                                            {record.members.length} consolidated
                                        </span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// A record a linked document's claims would create or change, waiting on the Review page.
function PendingBadge({ record }: { record: { pending?: 'new' | 'change'; review_id?: string | null } }) {
    return (
        <Link
            to={record.review_id ? `/review?id=${record.review_id}` : '/review'}
            onClick={e => e.stopPropagation()}
            className="px-1.5 py-0.5 rounded-md border border-dashed border-state-warning text-graphite bg-state-warning/15 text-[11px] font-medium hover:bg-state-warning/30"
        >
            {record.pending === 'new' ? 'Pending review' : 'Change pending review'}
        </Link>
    );
}

function Suggestions({ items, onDecide, busy }: {
    items: KnowledgeSuggestion[];
    onDecide: (id: string, verdict: 'accept' | 'reject') => void;
    busy: string | null;
}) {
    if (items.length === 0) return null;
    return (
        <div className="mb-5 border border-softgrey rounded-xl divide-y divide-softgrey">
            <div className="px-4 py-2.5 bg-offwhite rounded-t-xl">
                <p className="text-[13px] font-semibold text-graphite">
                    {items.length === 1 ? 'One possible duplicate' : `${items.length} possible duplicates`}
                </p>
                <p className="text-[12px] text-coolslate">
                    These look like the same thing. Consolidate them and they read as one record everywhere.
                </p>
            </div>
            {items.map(item => (
                <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0 text-[13px]">
                        <span className="text-graphite font-medium">{item.records[0]?.name}</span>
                        <span className="text-coolslate"> and </span>
                        <span className="text-graphite font-medium">{item.records[1]?.name}</span>
                        {item.evidence?.matched && (
                            <span className="block text-[11px] text-coolslate">
                                matching {String(item.rule ?? 'value').replace(/_/g, ' ')}: {String(item.evidence.matched)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={() => onDecide(item.id, 'accept')}
                            disabled={busy === item.id}
                            className="px-2.5 py-1 text-[12px] rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
                        >
                            Same thing
                        </button>
                        <button
                            onClick={() => onDecide(item.id, 'reject')}
                            disabled={busy === item.id}
                            className="px-2.5 py-1 text-[12px] rounded-lg border border-softgrey text-graphite hover:bg-offwhite disabled:opacity-50"
                        >
                            Not the same
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function MergeBar({ picked, records, lead, onLead, onMerge, onClear, busy, error }: {
    picked: string[];
    records: KnowledgeRecord[];
    lead: string | null;
    onLead: (key: string) => void;
    onMerge: () => void;
    onClear: () => void;
    busy: boolean;
    error: string | null;
}) {
    const names = picked.map(key => records.find(r => r.key === key)?.name ?? key);
    if (names.length === 0) return null;
    return (
        <div className="mb-5 border border-brand/30 bg-brand/5 rounded-xl px-4 py-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-[13px] text-graphite min-w-0">
                    {names.length === 1 ? (
                        <><span className="font-medium">{names[0]}</span> selected — pick another to
                        consolidate them.</>
                    ) : (
                        <>Consolidate <span className="font-medium">{names.join(', ')}</span>? The
                        one you read as supplies the name and details; the others keep
                        theirs on the record.</>
                    )}
                </p>
                <div className="flex items-center gap-2 shrink-0">
                    {names.length > 1 && (
                        <label className="text-[12px] text-coolslate flex items-center gap-1.5">
                            Read as
                            <select
                                aria-label="Which record to read as"
                                value={lead ?? picked[0]}
                                onChange={e => onLead(e.target.value)}
                                className="px-2 py-1 text-[12px] border border-softgrey rounded-lg bg-white text-graphite"
                            >
                                {picked.map((key, i) => (
                                    <option key={key} value={key}>{names[i]}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <button
                        onClick={onMerge}
                        disabled={busy || names.length < 2}
                        className="px-2.5 py-1 text-[12px] rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
                    >
                        Same thing
                    </button>
                    <button
                        onClick={onClear}
                        disabled={busy}
                        className="px-2.5 py-1 text-[12px] rounded-lg border border-softgrey text-graphite hover:bg-offwhite disabled:opacity-50"
                    >
                        Clear
                    </button>
                </div>
            </div>
            {error && <p className="text-[12px] text-red-600 mt-2">{error}</p>}
        </div>
    );
}

type KnowledgeTab = 'entities' | 'relationships' | 'library';
const KNOWLEDGE_TABS: KnowledgeTab[] = ['entities', 'relationships', 'library'];

export default function KnowledgePage() {
    const { authFetch } = useAuth();
    const location = useLocation();
    const [summary, setSummary] = useState<KnowledgeLabelSummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [detail, setDetail] = useState<LabelDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [target, setTarget] = useState<{ label: string; key: string } | null>(null);
    const [suggestions, setSuggestions] = useState<KnowledgeSuggestion[]>([]);
    const [deciding, setDeciding] = useState<string | null>(null);
    const [version, setVersion] = useState(0);
    const [relationships, setRelationships] = useState<KnowledgeRelationshipType[]>([]);
    const [picked, setPicked] = useState<string[]>([]);
    const [lead, setLead] = useState<string | null>(null);
    const [merging, setMerging] = useState(false);
    const [mergeError, setMergeError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<KnowledgeTab>(() => {
        const t = new URLSearchParams(location.search).get('tab') as KnowledgeTab | null;
        return t && KNOWLEDGE_TABS.includes(t) ? t : 'entities';
    });

    useEffect(() => {
        let stale = false;
        authFetch('/knowledge/edges')
            .then(r => r.ok ? r.json() : [])
            .then((data: KnowledgeRelationshipType[]) => { if (!stale) setRelationships(data); })
            .catch(() => {});
        return () => { stale = true; };
    }, []);

    useEffect(() => {
        let stale = false;
        authFetch('/knowledge/nodes')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load knowledge')))
            .then((data: KnowledgeLabelSummary[]) => {
                if (stale) return;
                setSummary(data);
                setSelected(data[0]?.label ?? null);
            })
            .catch(err => { if (!stale) { setError(err.message); setSummary([]); } });
        return () => { stale = true; };
    }, []);

    useEffect(() => {
        setPicked([]);
        setLead(null);
        setMergeError(null);
    }, [selected]);

    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        let stale = false;
        setDetailLoading(true);
        authFetch(`/knowledge/records/${encodeURIComponent(selected)}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load records')))
            .then((data: LabelDetail) => { if (!stale) setDetail(data); })
            .catch(() => { if (!stale) setDetail(null); })
            .finally(() => { if (!stale) setDetailLoading(false); });
        return () => { stale = true; };
    }, [selected, version]);

    useEffect(() => {
        if (!selected) { setSuggestions([]); return; }
        let stale = false;
        authFetch(`/knowledge/suggestions?label=${encodeURIComponent(selected)}`)
            .then(r => r.ok ? r.json() : [])
            .then((data: KnowledgeSuggestion[]) => { if (!stale) setSuggestions(data); })
            .catch(() => { if (!stale) setSuggestions([]); });
        return () => { stale = true; };
    }, [selected, version]);

    // Consolidating changes counts as well as rows, so refresh both.
    const refresh = useCallback(() => {
        setVersion(v => v + 1);
        authFetch('/knowledge/nodes')
            .then(r => r.ok ? r.json() : [])
            .then((data: KnowledgeLabelSummary[]) => setSummary(data))
            .catch(() => {});
    }, [authFetch]);

    const pick = useCallback((key: string) => {
        setMergeError(null);
        setPicked(keys => {
            const next = keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key];
            // A record that is no longer selected cannot be the one we read as.
            setLead(current => (current && next.includes(current) ? current : null));
            return next;
        });
    }, []);

    const merge = useCallback(async () => {
        if (!selected || picked.length < 2) return;
        setMerging(true);
        setMergeError(null);
        try {
            const res = await authFetch('/knowledge/records/merge', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: selected, keys: picked, lead: lead ?? picked[0],
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not consolidate these records');
            }
            setPicked([]);
            setLead(null);
            refresh();
        } catch (err) {
            setMergeError(err instanceof Error ? err.message : String(err));
        } finally {
            setMerging(false);
        }
    }, [authFetch, lead, picked, refresh, selected]);

    const decide = useCallback(async (id: string, verdict: 'accept' | 'reject') => {
        setDeciding(id);
        try {
            const res = await authFetch(`/knowledge/suggestions/${id}/${verdict}`, { method: 'POST' });
            if (res.ok) refresh();
        } finally {
            setDeciding(null);
        }
    }, [authFetch, refresh]);

    if (summary === null) return <PageLoader label="Loading knowledge..." />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold">Records</h3>
                <p className="text-sm text-coolslate mt-1">
                    What we've learned about the company from your connected sources - organisations,
                    invoices, deals and more, as records and the relationships between them. Where several
                    sources describe the same thing it reads as one record - open it to see what each
                    contributed. The Library tab lists everything the record can hold.
                </p>
            </div>

            {error && <InlineError message={error} />}

            <Tabs
                size="sm"
                items={[
                    { id: 'entities' as const, label: 'Entities', icon: Database },
                    { id: 'relationships' as const, label: 'Relationships', icon: GitBranch },
                    { id: 'library' as const, label: 'Library', icon: BookOpen },
                ]}
                active={activeTab}
                onChange={setActiveTab}
            />

            {activeTab === 'entities' && summary.length === 0 && (
                <p className="navaris-empty">
                    Nothing is on the record yet. Connect a system or file a document to start building it.
                </p>
            )}

            {activeTab === 'entities' && summary.length > 0 && (
            <div>
                <select
                    value={selected ?? ''}
                    onChange={e => setSelected(e.target.value)}
                    className="mb-5 w-full max-w-xs px-3 py-2 text-sm border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand/40 cursor-pointer"
                >
                    {[...summary].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                        <option key={s.label} value={s.label}>{s.name} ({s.count}{s.pending ? `, ${s.pending} pending` : ''})</option>
                    ))}
                </select>

                <Suggestions items={suggestions} onDecide={decide} busy={deciding} />

                <MergeBar
                    picked={picked}
                    records={detail?.records ?? []}
                    lead={lead}
                    onLead={setLead}
                    onMerge={merge}
                    onClear={() => { setPicked([]); setLead(null); }}
                    busy={merging}
                    error={mergeError}
                />

                {detailLoading ? (
                    <div className="flex justify-center py-10">
                        <Loader className="w-6 h-6 animate-spin text-brand" />
                    </div>
                ) : detail ? (
                    <>
                        <div className="flex items-center gap-2 mb-3">
                            <Database className="w-4 h-4 text-coolslate" />
                            <h4 className="navaris-h3">{detail.label.name}</h4>
                            <code className="text-[11px] text-coolslate">{detail.label.label}</code>
                        </div>
                        <RecordsTable
                            detail={detail}
                            onOpen={key => setTarget({ label: detail.label.label, key })}
                            picked={picked}
                            onPick={pick}
                        />
                    </>
                ) : (
                    <p className="navaris-empty">Select a label to view its records.</p>
                )}
            </div>
            )}

            {activeTab === 'relationships' && (
                <div className="border border-softgrey rounded-xl divide-y divide-softgrey overflow-auto max-h-[65vh]">
                    {relationships.length === 0 && (
                        <p className="navaris-empty">No relationships on the record yet.</p>
                    )}
                    {[...relationships].sort((a, b) => a.type.localeCompare(b.type)).map(rel => (
                        <div key={rel.type} className="px-4 py-2.5 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <code className="text-[11px] text-brand-dark">{[rel.from].flat().join(' | ')}</code>
                                    <span className="text-coolslate text-xs">&rarr;</span>
                                    <code className="text-[11px] text-brand-dark">{[rel.to].flat().join(' | ')}</code>
                                    <span className="text-[11px] text-coolslate">({rel.type})</span>
                                </div>
                                {rel.description && (
                                    <p className="text-[12px] text-coolslate mt-0.5">{rel.description}</p>
                                )}
                            </div>
                            <span className="text-[11px] tabular-nums shrink-0 mt-0.5 text-coolslate">
                                {rel.count}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'library' && <KnowledgeLibrary />}

            {target && (
                <RecordDetailModal
                    target={target}
                    onNavigate={(label, key) => setTarget({ label, key })}
                    onClose={() => setTarget(null)}
                    onChanged={refresh}
                />
            )}
        </div>
    );
}
